import { ShoppingBasket } from 'lucide-react'

import { EmptyState, PosButton } from '@/components/ui'
import { useCart } from '@/contexts/CartContext'
import { useRegisterContext } from '@/contexts/RegisterContext'
import { askItemDetails } from '@/services/itemPrompt'
import { unitPriceFor } from '@/services/PricingService'
import { PosModel } from '@/types'
import { formatMoney } from '@/utils'

import { CartLine, PriceSource } from './CartLine'
import { CartTotals } from './CartTotals'
import { CustomerChip } from './CustomerChip'
import { RewardPrompts } from './RewardPrompts'

interface CartPanelProps {
  onPickCustomer: () => void
  onOrderDiscount: () => void
  onLineDiscount: (productId: string) => void
  onHold: () => void
  onClear: () => void
  onResume: () => void
  onPay: () => void
  heldCount: number
}

const priceSource = (product: PosModel.Product, customer?: PosModel.Customer): PriceSource => {
  const book = customer?.price_book_id ? product.book_prices?.[customer.price_book_id] : undefined
  if (book && book > 0) return 'price_book'
  const listed = product.branch_price ?? product.regular_price
  if (listed !== undefined && listed !== product.price) return 'entered'
  return product.branch_price ? 'branch' : 'product'
}

/** What the picked customer brings to the sale (§7.1, §7.3, §15.4). */
const customerTerms = (customer: PosModel.Customer) =>
  [
    customer.price_book_id && (customer.price_book ? `${customer.price_book} prices` : 'Price book prices'),
    customer.tier && `${customer.tier.name} ${customer.tier.discount_percentage}% off`,
    customer.credit_limit ? `Credit ${formatMoney(Math.max(customer.credit_limit - (customer.credit_used ?? 0), 0))} left` : null,
  ]
    .filter(Boolean)
    .join(' · ')

/**
 * Right-hand ticket: customer, lines (newest at the bottom, like a paper receipt), totals, then the actions with the
 * single dominant Pay pinned to the bottom.
 */
export const CartPanel = ({ onPickCustomer, onOrderDiscount, onLineDiscount, onHold, onClear, onResume, onPay, heldCount }: CartPanelProps) => {
  const cart = useCart()
  const { tax_rate } = useRegisterContext()
  const empty = cart.lines.length === 0
  const terms = cart.customer ? customerTerms(cart.customer) : ''

  const weigh = async (line: PosModel.CartLine) => {
    const details = await askItemDetails(line.product, { price: false, quantity: true })
    if (details?.quantity) cart.setQty(line.product._id, details.quantity)
  }

  return (
    <aside className="flex h-full min-h-0 flex-col bg-card">
      <div className="space-y-2 border-b border-border p-4">
        <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Customer</h2>
        <CustomerChip customer={cart.customer} onPick={onPickCustomer} onClear={() => cart.setCustomer(undefined)} />
        {terms && <p className="text-xs text-muted-foreground">{terms}</p>}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {empty ? (
          <EmptyState icon={ShoppingBasket} title="Cart is empty" className="h-full">
            Scan a barcode or tap a product to start a sale.
          </EmptyState>
        ) : (
          <ul aria-label="Cart items">
            {cart.lines.map((line) => (
              <CartLine
                key={line.product._id}
                line={line}
                unitPrice={unitPriceFor(line.product, cart.customer)}
                source={priceSource(line.product, cart.customer)}
                offerOff={cart.offerSavings[line.product._id] ?? 0}
                active={cart.activeId === line.product._id}
                onQty={(q) => cart.setQty(line.product._id, q)}
                onRemove={() => cart.remove(line.product._id)}
                onDiscount={() => onLineDiscount(line.product._id)}
                onWeigh={line.product.sold_by_unit ? () => weigh(line) : undefined}
              />
            ))}
          </ul>
        )}
        <RewardPrompts />
      </div>

      <div className="space-y-3 border-t border-border bg-subdued p-4">
        <CartTotals
          totals={cart.totals}
          taxRate={tax_rate}
          orderLabel={cart.memberDiscount?.reason}
          onAdjustment={empty ? undefined : cart.setAdjustment}
        />
        <div className="flex items-baseline justify-between border-t border-border pt-2">
          <span className="text-heading-md text-ink">Total</span>
          <span className="text-heading-lg tabular-nums text-ink">{formatMoney(cart.totals.total)}</span>
        </div>
        <div className="grid grid-cols-4 gap-2">
          <PosButton variant="outline" size="default" className="rounded-sm px-2" title="Order discount (F4)" onClick={onOrderDiscount} disabled={empty}>
            Discount
          </PosButton>
          <PosButton variant="outline" size="default" className="rounded-sm px-2" title="Hold (F6)" onClick={onHold} disabled={empty}>
            Hold
          </PosButton>
          <PosButton variant="outline" size="default" className="rounded-sm px-2" onClick={onClear} disabled={empty}>
            Clear
          </PosButton>
          <PosButton variant="outline" size="default" className="rounded-sm px-2" title="Held carts (F7)" onClick={onResume} disabled={heldCount === 0}>
            Resume
          </PosButton>
        </div>
        <PosButton size="xl" variant="success" className="w-full rounded-sm" onClick={onPay} disabled={empty} title="Pay (F9)">
          Pay {formatMoney(cart.totals.total)}
        </PosButton>
      </div>
    </aside>
  )
}
